<template>
    <div class="date-picker" v-disabled="disabled">
        <input type="text" :id="id" ref="input" class="form-control" v-c:form-control-sm="small" :value="date" @focus="onFocus" @blur="onBlur" @keyup.enter="onBlur">
        <popover ref="flyout" :autoCloseExcept="[$refs.input]" class="p-2" direction="auto" :offset-arrow="10">
            <div class="calendar-header">
                <icon name="menu-left" @click="prevMonth" v-disabled="min && flyoutDateObject.isSameOrBefore(minObject, 'month')"/>
                {{ flyoutDateObject.format("MMMM YYYY") }}
                <icon name="menu-right" @click="nextMonth" v-disabled="max && flyoutDateObject.isSameOrAfter(maxObject, 'month')"/>
            </div>
            <div class="calendar-row" v-for="(row, indexRow) in calendar" :key="indexRow">
                <btn v-for="(day, indexDay) in row"
                     :key="indexDay"
                     theme="none"
                     class="calendar-day"
                     :label="day.label"
                     v-disabled="day.disabled"
                     v-c:selected="day.selected"
                     v-c:today="day.today"
                     @click.prevent="selectDate(day.date)"
                />
            </div>
        </popover>
        <icon name="calendar" class="calendar-icon"/>
    </div>
</template>

<script>
    import moment from 'moment';

    export default {
        props: {
            value: String,
            id: {required: false, type: String},
            inputFormat: {type: String, default: 'YYYY-MM-DD'},
            displayFormat: {type: String, default: 'DD.MM.YYYY'},
            default: {type: String, default: 'now'},
            allowEmpty: {type: Boolean, default: true},
            small: {type: Boolean, default: false},
            disabled: {type: Boolean, default: false},
            min: {type: String, default: null},
            max: {type: String, default: null},
            disabledDays: {type: Array, default: () => []},
        },

        data() {
            return {
                lastValidDate: this.value,
                flyoutDate: null,
            };
        },

        computed: {
            dateObject() {
                return moment(this.value, this.inputFormat);
            },

            date: {
                get() {
                    if (this.value === '' || this.value === null) {
                        return null;
                    }
                    return this.dateObject.format(this.displayFormat);
                },
                set(value) {
                    if (value === "") {
                        this.$emit('input', this.allowEmpty ? null : this.getDefault(this.inputFormat));
                    } else {
                        let date = moment(value, this.displayFormat);
                        if (!date.isValid()) {
                            date = moment(this.lastValidDate, this.inputFormat);
                        }
                        this.$emit('input', date.format(this.inputFormat));
                        this.flyoutDate = date.clone().date(1).format(this.inputFormat);
                        this.lastValidDate = date.format(this.inputFormat);
                        this.$forceUpdate();
                    }
                },
            },

            minObject() {
                return this.min && moment(this.min === 'now' ? new Date : this.min, this.inputFormat);
            },

            maxObject() {
                return this.max && moment(this.max === 'now' ? new Date : this.max, this.inputFormat);
            },

            flyoutDateObject() {
                return moment(this.flyoutDate, this.inputFormat);
            },

            calendar() {
                let date = this.flyoutDateObject.clone();
                let rows = [];

                // set to first day of month and then to first day of week
                date.date(0).day(1);

                while (date.isSame(this.flyoutDateObject, 'week') || date.isSame(this.flyoutDateObject, 'month')) {
                    let row = [];

                    for (let i = 1; i <= 7; i++) {
                        row.push({
                            label: date.date(),
                            selected: date.isSame(this.dateObject, 'day'),
                            disabled: date.month() !== this.flyoutDateObject.month()
                                || (this.min && date.isBefore(this.minObject, 'day'))
                                || (this.max && date.isAfter(this.maxObject, 'day'))
                                || this.disabledDays.some(disabledDate => date.isSame(moment(disabledDate).format(this.inputFormat))),
                            today: moment().isSame(date, 'day'),
                            date: date.format(this.displayFormat),
                        });
                        date.add(1, "days");
                    }

                    rows.push(row);
                }

                return rows;
            },
        },

        methods: {
            getDefault(format) {
                let date = moment();
                if (this.default !== 'now') {
                    date = moment(this.default, this.inputFormat);
                }

                if (date.isBefore(this.minObject)) {
                    date = this.minObject;
                } else if (date.isAfter(this.maxObject)) {
                    date = this.maxObject;
                }

                return date.format(format);
            },

            async onFocus() {
                let date = this.date;
                if (date === null) {
                    date = this.date = this.getDefault(this.displayFormat);
                }
                this.flyoutDate = moment(date, this.displayFormat).date(1).format(this.inputFormat);
                await this.$nextTick();
                this.$refs.flyout.toggle(true);
            },

            onBlur() {
                if (this.$refs.input.value !== this.date) {
                    this.date = this.$refs.input.value;
                }
            },

            selectDate(date) {
                this.date = date;
                this.$nextTick(() => {
                    // delay until value is emitted through date.set
                    this.$refs.input.focus();
                });
            },

            prevMonth() {
                this.flyoutDate = this.flyoutDateObject.subtract(1, "months").format(this.inputFormat);
            },

            nextMonth() {
                this.flyoutDate = this.flyoutDateObject.add(1, "months").format(this.inputFormat);
            },
        },
    };
</script>